/**
 * Routing logic for the focused DrillView: given the full text a single
 * `/chat` turn streamed, decide what the frame should show next.
 *
 * A drill turn returns exactly one of two things:
 *   - a SPOT (the kickoff turn, or a "Next spot" turn) → the user acts
 *   - a VERDICT (prose grading the user's action) → we show it + CTAs
 *
 * Spot wins when both appear (a spot block means "act on this"); the
 * accompanying intro prose is incidental in the focused view. This is
 * the one place the spot-vs-verdict decision lives, so it's a pure
 * function we can unit-test without a live stream.
 */

import { parseSegments } from "@/features/chat/lib/messageSegments";
import type { ChoicesPayload, SpotPayload } from "@/shared/models";

export type StreamOutcome =
  | { kind: "spot"; spot: SpotPayload; choices?: ChoicesPayload }
  | { kind: "verdict"; text: string }
  | { kind: "empty" };

export function nextPhaseFromStream(buffer: string): StreamOutcome {
  const segments = parseSegments(buffer);

  const spotSeg = segments.find((s) => s.kind === "spot");
  if (spotSeg && spotSeg.kind === "spot") {
    const choicesSeg = segments.find((s) => s.kind === "choices");
    return {
      kind: "spot",
      spot: spotSeg.spot,
      choices:
        choicesSeg && choicesSeg.kind === "choices"
          ? choicesSeg.choices
          : undefined,
    };
  }

  const text = segments
    .filter((s) => s.kind === "text")
    .map((s) => (s.kind === "text" ? s.text : ""))
    .join("\n\n")
    .trim();
  if (text) return { kind: "verdict", text };

  return { kind: "empty" };
}
